import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Table, Empty } from 'antd';
import type { ColumnsType, TableProps, ColumnType } from 'antd/es/table';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Resizable } from 'react-resizable';
import WizrText from './WizrText';
import CustomerHealthCustomerCard from './popovers/CustomerHealth';
import TicketCountDisplay from './TicketCountDisplay';
import { dispatchCustomerHealthDetails, dispatchSingleConversationData, dispatchSingleAgentAssistConversation } from '../redux/actions/content';

// Add styles
const tableStyles = {
  table: {
    background: '#F8F9FC',
    borderRadius: '8px',
    padding: '16px',
  },
  headerCell: {
    background: 'transparent',
    color: '#6B7280',
    fontWeight: 600,
    fontSize: '14px',
    padding: '12px 16px',
    borderBottom: '1px solid #E5E7EB',
  },
  row: {
    '&:hover': {
      background: '#F3F4F6',
    },
  },
  cell: {
    padding: '12px 16px',
    fontSize: '14px',
  },
  scoreCell: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 8px',
    borderRadius: '16px',
    fontWeight: 600,
  },
  greenScore: {
    color: '#22C55E',
  },
  yellowScore: {
    color: '#FBBF24',
  },
  redScore: {
    color: '#EF4444',
  },
};

// Reuse the same WizrTableType from WizrTable
type WizrTableType =
  | 'folder'
  | 'file'
  | 'prompt'
  | 'history'
  | 'App'
  | 'AppTemplate'
  | 'AppDraft'
  | 'apphistory'
  | 'autosolvetesthistory'
  | 'autosolveevalshistory'
  | 'agentassisttesthistory'
  | 'bulkrun'
  | 'tenants'
  | 'users'
  | 'billing'
  | 'invoice'
  | 'products'
  | 'roles'
  | 'packages'
  | 'past_tickets'
  | 'integrations'
  | 'grouplisting'
  | 'intentcategorytable'
  | 'tagkeywordtable'
  | 'tickettable'
  | 'sentimenttable'
  | 'tickettypetable'
  | 'customfieldtable'
  | 'persona'
  | 'tickets'
  | 'integrationsSettings'
  | 'AppDraftMaster'
  | 'AppMaster'
  | 'faq'
  | 'customerHealth'
  | 'discover'
  | 'appInsights'
  | 'appInsightsSingle'
  | 'scheduler'
  | 'schedulerHistory'
  | 'agentperfomancetag'
  | 'agentInsights'
  | 'customerInteractions'
  | 'customerInteractionsAgentAssistHistory';

interface IWizrDragDropTable extends TableProps<any> {
  type: WizrTableType;
  tabSelected?: string;
  rowSelection?: any;
  customColumns?: ColumnsType<any>;  // Add support for custom columns
  handleRowClick?: (record: any) => void; 
  handleRowAgentAssistClick?: (record: any) => void; // Add support for row click handler
  sizeChangerOnly?: boolean; // Hide page numbers and controls, show only size changer
}

interface DragDropHeaderCellProps {
  children?: React.ReactNode;
  moveColumn: (dragIndex: number, hoverIndex: number) => void;
  index: number;
  width?: number | string;
  onResize?: (e: React.SyntheticEvent, { size }: { size: { width: number; height: number } }) => void;
  className?: string;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler<any>;
  onMouseEnter?: React.MouseEventHandler<any>;
  onMouseLeave?: React.MouseEventHandler<any>;
}

const DraggableHeaderCell: React.FC<DragDropHeaderCellProps> = ({
  children,
  moveColumn,
  index,
  width,
  onResize,
  className,
  style,
  ...restProps
}) => {
  const ref = React.useRef<HTMLTableCellElement>(null);
  const [isResizing, setIsResizing] = useState(false);

  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: 'column',
    collect: monitor => {
      const { index: dragIndex } = monitor.getItem() || {};
      if (dragIndex === index) {
        return {};
      }
      return {
        isOver: monitor.isOver(),
        dropClassName: dragIndex < index ? 'drop-over-right' : 'drop-over-left',
      };
    },
    drop: (item: { index: number }) => {
      if (item.index !== index) {
        moveColumn(item.index, index);
      }
    },
  });

  const [, drag] = useDrag({
    type: 'column',
    item: { index },
    canDrag: () => !isResizing,
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drop(drag(ref));

  const combinedClassName = `${className || ''} ${isOver ? dropClassName : ''}`.trim();
  const combinedStyle = { cursor: isResizing ? 'col-resize' : 'move', ...style };

  if (!width) {
    return <th ref={ref} className={combinedClassName} style={combinedStyle} {...restProps}>{children}</th>;
  }

  return (
    <Resizable
      width={typeof width === 'string' ? parseInt(width) : width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          onClick={e => e.stopPropagation()}
          onMouseDown={e => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          aria-label="Resize column"
        />
      }
      onResize={onResize}
      onResizeStart={() => setIsResizing(true)}
      onResizeStop={() => setIsResizing(false)}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th
        ref={ref}
        className={combinedClassName}
        style={{ ...combinedStyle, width }}
        {...restProps}
      >
        {children}
      </th>
    </Resizable>
  );
};

const WizrDragDropTable = ({
  type,
  tabSelected,
  rowSelection,
  customColumns,
  handleRowClick,
  handleRowAgentAssistClick,
  sizeChangerOnly,
  ...props 
}: IWizrDragDropTable): JSX.Element => {
  const getScoreStyle = (score: number): React.CSSProperties => {
    if (score >= 80) return { ...tableStyles.scoreCell, ...tableStyles.greenScore };
    if (score >= 60) return { ...tableStyles.scoreCell, ...tableStyles.yellowScore };
    return { ...tableStyles.scoreCell, ...tableStyles.redScore };
  };

  const getTimeStyle = (hours: number): React.CSSProperties => {
    if (hours <= 24) return { ...tableStyles.scoreCell, ...tableStyles.greenScore };
    if (hours <= 48) return { ...tableStyles.scoreCell, ...tableStyles.yellowScore };
    return { ...tableStyles.scoreCell, ...tableStyles.redScore };
  };

  const tableColumnConfigs: Record<WizrTableType, ColumnsType<any>> = {
    folder: [],
    file: [],
    prompt: [],
    history: [],
    App: [],
    AppTemplate: [],
    AppDraft: [],
    apphistory: [],
    customerInteractions: [],  // Empty config since this uses customColumns
    autosolvetesthistory: [
      {
        title: 'Date',
        dataIndex: 'date',
        key: 'date',
        width: 150,
        sorter: (a: any, b: any) => {
          if (!a.date || !b.date) return 0;
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        },
        render: (text: any) => <WizrText type='body2'>{text}</WizrText>,
      },
      {
        title: 'User ID',
        dataIndex: 'user_id',
        key: 'user_id',
        width: 120,
        sorter: (a: any, b: any) => {
          if (!a.user_id || !b.user_id) return 0;
          return a.user_id.localeCompare(b.user_id);
        },
        render: (text: any) => <WizrText type='body2'>{text}</WizrText>,
      },
      {
        title: 'Session Input',
        dataIndex: 'session_input',
        key: 'session_input',
        width: 300,
        sorter: (a: any, b: any) => {
          if (!a.session_input || !b.session_input) return 0;
          return a.session_input.localeCompare(b.session_input);
        },
        render: (text: any) => (
          <div style={{
            maxWidth: '280px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            cursor: 'pointer'
          }}>
            <WizrText type='body2'>{text}</WizrText>
          </div>
        ),
      },
      {
        title: 'Conversation ID',
        dataIndex: 'conversation_id',
        key: 'conversation_id',
        width: 200,
        sorter: (a: any, b: any) => {
          if (!a.conversation_id || !b.conversation_id) return 0;
          return a.conversation_id.localeCompare(b.conversation_id);
        },
        render: (text: any) => <WizrText type='body2'>{text}</WizrText>,
      },
      {
        title: 'Duration',
        dataIndex: 'duration',
        key: 'duration',
        width: 120,
        sorter: (a: any, b: any) => {
          if (!a.duration || !b.duration) return 0;
          return parseFloat(a.duration) - parseFloat(b.duration);
        },
        render: (text: any) => <WizrText type='body2'>{text}</WizrText>,
      },
      {
        title: 'Start Time',
        dataIndex: 'start_time',
        key: 'start_time',
        width: 150,
        sorter: (a: any, b: any) => {
          if (!a.start_time || !b.start_time) return 0;
          return new Date(a.start_time).getTime() - new Date(b.start_time).getTime();
        },
        render: (text: any) => <WizrText type='body2'>{text}</WizrText>,
      },
      {
        title: '',
        key: 'actions',
        dataIndex: 'detailedLogs',
        width: 100,
        render: (text: any) => text,
      },
    ],
    autosolveevalshistory: [
      {
        title: 'Dataset Name',
        dataIndex: 'dataset_name',
        key: 'dataset_name',
        width: 250,
        sorter: (a: any, b: any) => {
          if (!a.dataset_name || !b.dataset_name) return 0;
          return a.dataset_name.localeCompare(b.dataset_name);
        },
        render: (text: any) => (
          <div style={{
            maxWidth: '240px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            <WizrText type='body2'>{text}</WizrText>
          </div>
        ),
      },
      {
        title: 'Duration',
        dataIndex: 'duration',
        key: 'duration',
        width: 120,
        sorter: (a: any, b: any) => {
          // Extract minutes from duration string for sorting
          const getMinutes = (durationStr: string): number => {
            if (!durationStr) return 0;
            const hrIdx = durationStr.indexOf('hr');
            const minIdx = durationStr.indexOf('min');
            const hours = hrIdx !== -1 ? (parseInt(durationStr.slice(0, hrIdx)) || 0) : 0;
            const minutes = minIdx !== -1 ? (parseInt(durationStr.slice(hrIdx !== -1 ? hrIdx + 2 : 0, minIdx)) || 0) : 0;
            return hours * 60 + minutes;
          };
          return getMinutes(a.duration) - getMinutes(b.duration);
        },
        render: (text: any) => <WizrText type='body2'>{text}</WizrText>,
      },
      {
        title: 'Start Time',
        dataIndex: 'start_time',
        key: 'start_time',
        width: 150,
        sorter: (a: any, b: any) => {
          const ta =
            typeof a.start_time_ms === 'number'
              ? a.start_time_ms
              : new Date(a.start_time).getTime();
          const tb =
            typeof b.start_time_ms === 'number'
              ? b.start_time_ms
              : new Date(b.start_time).getTime();
          const na = Number.isNaN(ta) ? 0 : ta;
          const nb = Number.isNaN(tb) ? 0 : tb;
          return na - nb;
        },
        render: (text: any) => <WizrText type='body2'>{text}</WizrText>,
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        sorter: (a: any, b: any) => {
          // Sort by status text if available
          const aStatus = a.status?.props?.children?.[0]?.props?.children?.[0] || '';
          const bStatus = b.status?.props?.children?.[0]?.props?.children?.[0] || '';
          return aStatus.localeCompare(bStatus);
        },
        render: (text: any) => text, // Render the WizrTag component
      },
      {
        title: 'Report',
        dataIndex: 'report',
        key: 'report',
        width: 100,
        render: (text: any) => text, // Render the View link component
      },
    ],
    customerInteractionsAgentAssistHistory: [],  // Empty config since this uses customColumns
    agentassisttesthistory: [
      {
        title: 'Date',
        dataIndex: 'date',
        key: 'date',
        width: 150,
        sorter: (a: any, b: any) => {
          if (!a.date || !b.date) return 0;
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        },
        render: (text: any) => <WizrText type='body2'>{text}</WizrText>,
      },
      {
        title: 'User ID',
        dataIndex: 'user_id',
        key: 'user_id',
        width: 120,
        sorter: (a: any, b: any) => {
          if (!a.user_id || !b.user_id) return 0;
          return a.user_id.localeCompare(b.user_id);
        },
        render: (text: any) => <WizrText type='body2'>{text}</WizrText>,
      },
      {
        title: 'Session Input',
        dataIndex: 'session_input',
        key: 'session_input',
        width: 300,
        sorter: (a: any, b: any) => {
          if (!a.session_input || !b.session_input) return 0;
          return a.session_input.localeCompare(b.session_input);
        },
        render: (text: any) => (
          <div style={{ 
            maxWidth: '280px', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            cursor: 'pointer'
          }}>
            <WizrText type='body2'>{text}</WizrText>
          </div>
        ),
      },
      {
        title: 'Conversation ID',
        dataIndex: 'conversation_id',
        key: 'conversation_id',
        width: 200,
        sorter: (a: any, b: any) => {
          if (!a.conversation_id || !b.conversation_id) return 0;
          return a.conversation_id.localeCompare(b.conversation_id);
        },
        render: (text: any) => <WizrText type='body2'>{text}</WizrText>,
      },
      {
        title: 'Duration',
        dataIndex: 'duration',
        key: 'duration',
        width: 120,
        sorter: (a: any, b: any) => {
          if (!a.duration || !b.duration) return 0;
          return parseFloat(a.duration) - parseFloat(b.duration);
        },
        render: (text: any) => <WizrText type='body2'>{text}</WizrText>,
      },
      {
        title: 'Start Time',
        dataIndex: 'start_time',
        key: 'start_time',
        width: 150,
        sorter: (a: any, b: any) => {
          if (!a.start_time || !b.start_time) return 0;
          return new Date(a.start_time).getTime() - new Date(b.start_time).getTime();
        },
        render: (text: any) => <WizrText type='body2'>{text}</WizrText>,
      },
      {
        title: '',
        key: 'actions',
        dataIndex: 'detailedLogs',
        width: 100,
        render: (text: any) => text,
      },
    ],
    bulkrun: [],
    tenants: [],
    users: [],
    billing: [],
    invoice: [],
    products: [],
    roles: [],
    packages: [],
    past_tickets: [],
    integrations: [],
    grouplisting: [],
    intentcategorytable: [],
    tagkeywordtable: [],
    tickettable: [],
    sentimenttable: [],
    tickettypetable: [],
    customfieldtable: [],
    persona: [],
    tickets: [],
    integrationsSettings: [],
    AppDraftMaster: [],
    AppMaster: [],
    faq: [],
    customerHealth: [
      {
        title: 'Customer Name',
        dataIndex: 'customer',
        key: 'customer',
        width: 200,
        sorter: (a: any, b: any) => {
          if (!a.customer && !b.customer) return 0;
          if (!a.customer) return 1;
          if (!b.customer) return -1;
          return a.customer.localeCompare(b.customer);
        },
        render: (text: string, record: any) =>
          <div style={{ cursor: 'pointer' }}>
            < CustomerHealthCustomerCard details={record} />
          </div>,
      },
      {
        title: 'Overall Health',
        dataIndex: 'health',
        key: 'health',
        width: 150,
        sorter: (a: any, b: any) => {
          if (a.health === null || a.health === undefined) return 1;
          if (b.health === null || b.health === undefined) return -1;
          return Number(a.health) - Number(b.health);
        },
        render: (score: number) => (
          <span style={getScoreStyle(score)}>{score}</span>
        ),
      },
      {
        title: 'Sentiment',
        dataIndex: 'sentiment',
        key: 'sentiment',
        width: 150,
        sorter: (a: any, b: any) => {
          if (a.sentiment === null || a.sentiment === undefined) return 1;
          if (b.sentiment === null || b.sentiment === undefined) return -1;
          return Number(a.sentiment) - Number(b.sentiment);
        },
        render: (score: number) => (
          <span style={getScoreStyle(score)}>{score}</span>
        ),
      },
      {
        title: 'Escalations',
        dataIndex: 'escalation',
        key: 'escalation',
        width: 150,
        sorter: (a: any, b: any) => {
          if (a.escalation === null || a.escalation === undefined) return 1;
          if (b.escalation === null || b.escalation === undefined) return -1;
          return Number(a.escalation) - Number(b.escalation);
        },
        render: (count: number) => (
          <span style={getScoreStyle(100 - (count * 10))}>{count}</span>
        ),
      },
      {
        title: 'Time to Resolve',
        dataIndex: 'time_for_resolve',
        key: 'time_for_resolve',
        width: 150,
        sorter: (a: any, b: any) => {
          if (a.time_for_resolve === null || a.time_for_resolve === undefined) return 1;
          if (b.time_for_resolve === null || b.time_for_resolve === undefined) return -1;
          return Number(a.time_for_resolve) - Number(b.time_for_resolve);
        },
        render: (hours: number) => (
          <span style={getTimeStyle(hours)}>{hours} hrs</span>
        ),
      },
      {
        title: 'Total Tickets',
        dataIndex: 'total_tickets',
        key: 'total_tickets',
        width: 150,
        sorter: (a: any, b: any) => {
          if (a.total_tickets === null || a.total_tickets === undefined) return 1;
          if (b.total_tickets === null || b.total_tickets === undefined) return -1;
          return Number(a.total_tickets) - Number(b.total_tickets);
        },
        render: (count: number) => (
          //   <WizrText type='body2'>{count}</WizrText>
          <TicketCountDisplay text={count} type='total_tickets' />
        ),
      },
      {
        title: 'Open Tickets',
        dataIndex: 'open_tickets',
        key: 'open_tickets',
        width: 150,
        sorter: (a: any, b: any) => {
          if (a.open_tickets === null || a.open_tickets === undefined) return 1;
          if (b.open_tickets === null || b.open_tickets === undefined) return -1;
          return Number(a.open_tickets) - Number(b.open_tickets);
        },
        render: (count: number) => (
          //   <WizrText type='body2'>{count}</WizrText>
          <TicketCountDisplay text={count} type='open_tickets' />
        ),
      },
    ],
    discover: [
      ...Object.keys(props.dataSource?.[0] || {}).map((key) => ({
        title: key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
        dataIndex: key,
        key: key,
        width: Math.max(150, key.length * 12), // Dynamically calculate width based on header text length
        sorter: (a: any, b: any) => {
          if (a[key] === null || a[key] === undefined) return 1;
          if (b[key] === null || b[key] === undefined) return -1;

          // Handle numeric values
          if (!isNaN(a[key]) && !isNaN(b[key])) {
            return Number(a[key]) - Number(b[key]);
          }

          // Handle string values
          return String(a[key]).localeCompare(String(b[key]));
        }
      })) as ColumnType<any>[],
    ],
    appInsights: [],
    appInsightsSingle: [],
    scheduler: [],
    schedulerHistory: [],
    agentperfomancetag: [],
    agentInsights: [],
  };

  // Use customColumns if provided, otherwise use the configured columns
  const initialColumns = customColumns || tableColumnConfigs[type] || [];
  const [columns, setColumns] = useState<ColumnsType<any>>(initialColumns);
  const columnsRef = useRef<ColumnsType<any>>(initialColumns);
  const discoverKeysSignatureRef = useRef<string>('');

  // Update columns when type or customColumns change (non-discover only)
  useEffect(() => {
    if (type !== 'discover') {
      const newColumns = customColumns || tableColumnConfigs[type] || [];
      setColumns(newColumns);
      columnsRef.current = newColumns;
    }
  }, [type, customColumns]);

  // For 'discover', only (re)generate columns when the set of keys changes.
  useEffect(() => {
    if (type !== 'discover') return;
    const firstRow = props.dataSource?.[0] || {};
    const dataKeys = Object.keys(firstRow);
    const signature = dataKeys.join('|');
    if (signature === discoverKeysSignatureRef.current) {
      return; // No change in keys; keep current order
    }

    // Base/generator for columns from keys
    const baseColumns = dataKeys.map((key) => ({
      title: key,
      dataIndex: key,
      key: key,
      width: 150,
      sorter: (a: any, b: any) => {
        if (a[key] == null) return 1;
        if (b[key] == null) return -1;
        if (!isNaN(a[key]) && !isNaN(b[key])) {
          return Number(a[key]) - Number(b[key]);
        }
        return String(a[key]).localeCompare(String(b[key]));
      }
    })) as ColumnsType<any>;

    // Preserve previous order where possible
    const prev = columnsRef.current || [];
    if (prev.length > 0) {
      const map = new Map((baseColumns as any[]).map((c: any) => [c.key, c]));
      const ordered: any[] = [];
      prev.forEach((c: any) => {
        const next = map.get(c.key);
        if (next) {
          ordered.push(next);
          map.delete(c.key);
        }
      });
      // Append any new columns
      map.forEach((val) => ordered.push(val));
      setColumns(ordered as ColumnsType<any>);
      columnsRef.current = ordered as ColumnsType<any>;
    } else {
      setColumns(baseColumns);
      columnsRef.current = baseColumns;
    }
    discoverKeysSignatureRef.current = signature;
  }, [type, props.dataSource]);

  const handleResize = (index: number) => (
    e: React.SyntheticEvent,
    { size }: { size: { width: number; height: number } }
  ) => {
    const nextColumns = [...columns];
    nextColumns[index] = {
      ...nextColumns[index],
      width: size.width,
    };
    setColumns(nextColumns);
  };

  const customerHealthNavigation = (record: any) => {
    dispatchCustomerHealthDetails(record);
  };

  // Add the customer interaction handler
  const customerInteractionAutosolveHistory = (record: any) => {
    dispatchSingleConversationData({ bot_id: record.bot_id, conversation_id: record.conversation_id })
    if (handleRowClick) {
      handleRowClick(record)
    }
  }

  const customerInteractionAgentAssistHistory = (record: any) => {
    dispatchSingleAgentAssistConversation({conversation_id: record.conversation_id})
    if (handleRowAgentAssistClick) {  
      handleRowAgentAssistClick(record)
    }
  }

  const moveColumn = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      setColumns(prevColumns => {
        const newColumns = [...prevColumns];
        const item = newColumns[dragIndex];
        newColumns.splice(dragIndex, 1);
        newColumns.splice(hoverIndex, 0, item);
        columnsRef.current = newColumns;
        return newColumns;
      });
    },
    []
  );

  const draggableColumns = React.useMemo(() => {
    return columns.map((col, index) => {
      const column = col as ColumnType<any>;
      return {
        ...column,
        onHeaderCell: (column: ColumnType<any>): DragDropHeaderCellProps => ({
          width: column.width,
          onResize: handleResize(index),
          moveColumn,
          index,
          style: { cursor: 'move' },
        }),
        sorter: column.sorter,
      };
    }) as ColumnsType<any>;
  }, [columns, moveColumn, handleResize]);

  return (
    <DndProvider backend={HTML5Backend}>
      <Table
        {...props}
        columns={draggableColumns}
        components={{
          header: {
            cell: DraggableHeaderCell as any,
          },
        }}
        scroll={props.scroll ?? { x: 'max-content' }}
        rowSelection={rowSelection}
        style={{ background: '#FFFFFF' }}
        className={`wizr-drag-drop-table ${sizeChangerOnly ? 'sizeonly' : ''} ${props.className || ''}`.trim()}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No data"
            />
          ),
        }}
        pagination={
          props.pagination === false
            ? false  // Respect when pagination is explicitly disabled
            : {
              total: props.dataSource?.length || 0,
              pageSize: 20,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total: number, range: [number, number]) =>
                `${range[0]}-${range[1]} of ${total} items`,
              pageSizeOptions: ['10', '20', '50', '100'],
              position: ['bottomRight'],
              ...(typeof props.pagination === 'object' ? props.pagination : {}),
            }
        }
        onRow={(record, rowIndex) => ({
          onClick: () => {
            if (type === 'customerHealth') {
              customerHealthNavigation(record);
            } else if (type === 'customerInteractions') {
              customerInteractionAutosolveHistory(record);
            } else if (handleRowClick) {
              handleRowClick(record);
            }
            else if (type === 'customerInteractionsAgentAssistHistory') {
              customerInteractionAgentAssistHistory(record);
            }
            else if (handleRowAgentAssistClick) {
              handleRowAgentAssistClick(record);
            }
          },
        })}
      />
      <style dangerouslySetInnerHTML={{
        __html: `
        .wizr-drag-drop-table .ant-table-thead > tr > th {
          border-right: 1px solid #E5E7EB;
          border-bottom: 1px solid #E5E7EB;
          transition: background 0.2s, border-color 0.2s;
          padding: 12px 16px;
          position: relative;
        }
        
        .wizr-drag-drop-table .ant-table-thead > tr > th:last-child {
          border-right: none;
        }

        .wizr-drag-drop-table .ant-table-tbody > tr > td {
          border-right: 1px solid #E5E7EB;
          padding: 12px 16px;
        }

        .wizr-drag-drop-table .ant-table-tbody > tr > td:last-child {
          border-right: none;
        }

        .wizr-drag-drop-table .ant-table-tbody > tr:hover > td {
          background: #F3F4F6;
        }

        .drop-over-right {
          border-right: 2px dashed #1890ff !important;
        }
        
        .drop-over-left {
          border-left: 2px dashed #1890ff !important;
        }

        .react-resizable {
          position: relative;
          background-clip: padding-box;
        }

        .react-resizable-handle {
          position: absolute;
          width: 8px;
          height: 100% !important;
          top: 0 !important;
          right: 0px;
          cursor: col-resize;
          z-index: 10;
          border-right: 2px solid transparent;
          background: transparent !important; /* hide default corner icon */
          background-image: none !important;  /* hide default corner icon */
        }

        .react-resizable-handle:hover {
          border-right: 2px solid #1890ff;
        }

        .react-resizable-handle:active {
          border-right: 2px solid #1890ff;
        }

        /* Style the sort icons */
        .ant-table-column-sorter {
          margin-left: 8px;
          color: #999B9F;
        }

        .ant-table-column-sorter-up.active,
        .ant-table-column-sorter-down.active {
          color: #1890ff;
        }

        .ant-table-column-sorter-inner {
          margin-top: 0;
        }

        .ant-table-column-title {
          display: flex;
          align-items: center;
        }

        /* Handle cursor for dragging and sorting */
        .wizr-drag-drop-table .ant-table-thead > tr > th {
          cursor: move;
        }

        .wizr-drag-drop-table .ant-table-column-sorters {
          cursor: move;
        }

        .wizr-drag-drop-table .ant-table-column-sorter {
          cursor: pointer;
          position: relative;
          z-index: 2;
          pointer-events: auto;
        }

        /* Ensure sort area is clickable */
        .wizr-drag-drop-table .ant-table-column-sorters .ant-table-column-title {
          pointer-events: none;
        }

        /* Pagination Styles */
        .wizr-drag-drop-table .ant-pagination {
          margin: 16px 0;
          padding-right: 16px;
        }

        .wizr-drag-drop-table .ant-pagination-item {
          border-radius: 4px;
          font-weight: 500;
        }

        .wizr-drag-drop-table .ant-pagination-item-active {
          background-color: #1890ff;
          border-color: #1890ff;
        }

        .wizr-drag-drop-table .ant-pagination-item-active a {
          color: white;
        }

        .wizr-drag-drop-table .ant-pagination-options {
          margin-left: 16px;
        }

        .wizr-drag-drop-table .ant-pagination-options-size-changer.ant-select {
          margin-right: 8px;
        }

        .wizr-drag-drop-table .ant-pagination-options-quick-jumper {
          margin-left: 8px;
        }

        .wizr-drag-drop-table .ant-pagination-options-quick-jumper input {
          border-radius: 4px;
          margin: 0 8px;
        }

        .wizr-drag-drop-table .ant-select-selector {
          border-radius: 4px !important;
        }
.wizr-drag-drop-table .ant-table table{
  table-layout: fixed;
}

.wizr-drag-drop-table .ant-table-thead > tr > th,
.wizr-drag-drop-table .ant-table-tbody > tr > td {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}



        /* Ensure empty state has enough height and is centered even when scroll is set */
        .wizr-drag-drop-table.ant-table-wrapper .ant-table-empty .ant-table-tbody > tr.ant-table-placeholder > td {
          height: 240px;
          padding: 0;
          background: #FFFFFF;
        }

        .wizr-drag-drop-table .ant-table-empty .ant-empty {
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0;
        }


        
        /* Pagination size-only mode: hide page items, prev/next, jumpers, total text */
        .wizr-drag-drop-table.sizeonly .ant-pagination-item,
        .wizr-drag-drop-table.sizeonly .ant-pagination-prev,
        .wizr-drag-drop-table.sizeonly .ant-pagination-next,
        .wizr-drag-drop-table.sizeonly .ant-pagination-jump-prev,
        .wizr-drag-drop-table.sizeonly .ant-pagination-jump-next,
        .wizr-drag-drop-table.sizeonly .ant-pagination-total-text,
        .wizr-drag-drop-table.sizeonly .ant-pagination-options-quick-jumper {
          display: none !important;
        }

      `}} />
    </DndProvider>
  );
};

export default WizrDragDropTable; 